import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Edit, Trash2, Plus, UserPlus, Link } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';
import { EmployeeFilters } from './EmployeeFilters';
import { SystemRole, SYSTEM_ROLE_LABELS } from '@/types/roles';

interface Employee {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position: string;
  department: string;
  status: string;
  hire_date: string;
  salary_amount: number | null;
  salary_currency: string;
  birth_date: string | null;
  address: string | null;
  manager_id: string | null;
  relay_id: string | null;
  user_id: string | null; // ID du compte utilisateur lié
  // Données du profil utilisateur lié
  profile?: {
    id: string;
    system_role: string;
    full_name: string;
    account_status: string;
  } | null;
}

export const EmployeesManager = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [createAccountDialog, setCreateAccountDialog] = useState(false);
  const [linkAccountDialog, setLinkAccountDialog] = useState(false);
  const [selectedEmployeeForAccount, setSelectedEmployeeForAccount] = useState<Employee | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [departments, setDepartments] = useState<{id: string, name: string}[]>([]);
  const [jobPositions, setJobPositions] = useState<{id: string, title: string}[]>([]);
  const [accountForm, setAccountForm] = useState({
    system_role: 'client' as SystemRole,
    password: ''
  });

  // Filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [employeeForm, setEmployeeForm] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    status: 'active',
    hire_date: new Date().toISOString().split('T')[0],
    salary_amount: '',
    salary_currency: 'USD',
    birth_date: '',
    address: '',
    manager_id: '',
    relay_id: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchJobPositions();
  }, []);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
    }
  };

  const fetchJobPositions = async () => {
    try {
      const { data, error } = await supabase
        .from('job_positions')
        .select('id, title')
        .order('title');
      
      if (error) throw error;
      setJobPositions(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des postes:', error);
    }
  };

  const generateEmployeeCode = async () => {
    try {
      const { data, error } = await supabase.rpc('generate_employee_code');
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur génération code employé:', error);
      return `EMP${String(employees.length + 1).padStart(3, '0')}`;
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      // Récupérer tous les utilisateurs avec system_role
      const { data: allUsers, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name, email, system_role')
        .order('full_name');
      
      if (usersError) {
        console.error('Erreur requête profiles:', usersError);
        throw usersError;
      }
      
      // Récupérer tous les employés avec user_id non null
      const { data: linkedEmployees, error: empError } = await supabase
        .from('employees')
        .select('user_id')
        .not('user_id', 'is', null);
      
      if (empError) {
        throw empError;
      }
      
      const linkedUserIds = new Set(linkedEmployees?.map(emp => emp.user_id) || []);
      
      // Filtrer les utilisateurs non liés (permettre tous les rôles)
      const availableUsers = allUsers?.filter(user => 
        !linkedUserIds.has(user.id)
      ) || [];
      
      setAvailableUsers(availableUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setAvailableUsers([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          profile:profiles!employees_user_id_fkey(
            id,
            system_role,
            full_name,
            account_status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error: any) {
      toast({
        title: t('error') || "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrer et trier les employés
  const filteredEmployees = employees
    .filter(employee => {
      if (statusFilter !== 'all' && employee.status !== statusFilter) return false;
      if (departmentFilter !== 'all' && employee.department !== departmentFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          employee.first_name.toLowerCase().includes(query) ||
          employee.last_name.toLowerCase().includes(query) ||
          employee.email?.toLowerCase().includes(query) ||
          employee.employee_code.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'position':
          comparison = a.position.localeCompare(b.position);
          break;
        case 'department':
          comparison = a.department.localeCompare(b.department);
          break;
        case 'hire_date':
          comparison = new Date(a.hire_date).getTime() - new Date(b.hire_date).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleClearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDepartmentFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleEditEmployee = (employee: Employee) => {
    setEmployeeForm({
      employee_code: employee.employee_code,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email || '',
      phone: employee.phone || '',
      position: employee.position,
      department: employee.department || 'Opérations',
      status: employee.status,
      hire_date: employee.hire_date,
      salary_amount: employee.salary_amount?.toString() || '',
      salary_currency: employee.salary_currency || 'USD',
      birth_date: employee.birth_date || '',
      address: employee.address || '',
      manager_id: employee.manager_id || '',
      relay_id: employee.relay_id || ''
    });
    setEditingEmployee(employee);
    setEmployeeDialog(true);
  };

  const handleDeleteEmployee = async (employeeId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;

    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeId);

      if (error) throw error;
      toast({ title: "Employé supprimé avec succès" });
      await fetchEmployees();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleCreateAccount = (employee: Employee) => {
    setSelectedEmployeeForAccount(employee);
    setAccountForm({
      system_role: 'client',
      password: ''
    });
    setCreateAccountDialog(true);
  };

  const handleLinkAccount = async (employee: Employee) => {
    setSelectedEmployeeForAccount(employee);
    await fetchAvailableUsers();
    setLinkAccountDialog(true);
  };

  const handleSaveAccount = async () => {
    if (!selectedEmployeeForAccount || !accountForm.password) {
      toast({
        title: t('error') || "Erreur",
        description: t('fill_required_fields') || "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    try {
      // Utiliser une Edge Function pour créer le compte utilisateur
      // (car supabase.auth.admin nécessite service_role_key côté serveur)
      const { data: createResult, error: createError } = await supabase.functions.invoke('create-employee-account', {
        body: {
          employee_id: selectedEmployeeForAccount.id,
          email: selectedEmployeeForAccount.email,
          password: accountForm.password,
          full_name: `${selectedEmployeeForAccount.first_name} ${selectedEmployeeForAccount.last_name}`,
          system_role: accountForm.system_role,
          phone: selectedEmployeeForAccount.phone
        }
      });

      if (createError) throw createError;

      toast({
        title: t('employees.success') || "Succès",
        description: `Compte utilisateur créé pour ${selectedEmployeeForAccount.first_name} ${selectedEmployeeForAccount.last_name}`
      });

      setCreateAccountDialog(false);
      setSelectedEmployeeForAccount(null);
      await fetchEmployees();
    } catch (error: any) {
      toast({
        title: t('error') || "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const linkEmployeeAccount = async () => {
    if (!selectedEmployeeForAccount || !selectedUserId) {
      toast({
        title: t('error') || "Erreur",
        description: "Veuillez sélectionner un utilisateur",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('employees')
        .update({ user_id: selectedUserId })
        .eq('id', selectedEmployeeForAccount.id);

      if (error) throw error;

      toast({
        title: t('employees.success') || "Succès",
        description: "Compte utilisateur lié avec succès"
      });

      setLinkAccountDialog(false);
      setSelectedUserId('');
      fetchEmployees();
    } catch (error) {
      console.error('Erreur lors de la liaison:', error);
      toast({
        title: t('error') || "Erreur",
        description: "Impossible de lier le compte utilisateur",
        variant: "destructive"
      });
    }
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.first_name || !employeeForm.last_name || !employeeForm.employee_code) {
      toast({
        title: t('error') || "Erreur",
        description: t('fill_required_fields') || "Veuillez remplir les champs obligatoires (prénom, nom, code employé)",
        variant: "destructive"
      });
      return;
    }

    try {
      const employeeData = {
        employee_code: employeeForm.employee_code,
        first_name: employeeForm.first_name,
        last_name: employeeForm.last_name,
        email: employeeForm.email || null,
        phone: employeeForm.phone || null,
        position: employeeForm.position,
        department: employeeForm.department,
        status: employeeForm.status,
        hire_date: employeeForm.hire_date,
        salary_amount: employeeForm.salary_amount ? parseFloat(employeeForm.salary_amount) : null,
        salary_currency: employeeForm.salary_currency,
        birth_date: employeeForm.birth_date || null,
        address: employeeForm.address || null,
        manager_id: employeeForm.manager_id || null,
        relay_id: employeeForm.relay_id || null
      };

      let result;
      if (editingEmployee) {
        result = await supabase
          .from('employees')
          .update(employeeData)
          .eq('id', editingEmployee.id);
      } else {
        result = await supabase
          .from('employees')
          .insert([employeeData]);
      }

      if (result.error) throw result.error;

      toast({
        title: editingEmployee ? "Employé mis à jour" : "Employé créé",
        description: `${employeeForm.first_name} ${employeeForm.last_name} a été ${editingEmployee ? 'mis à jour' : 'créé'} avec succès`
      });
      
      setEmployeeDialog(false);
      await resetEmployeeForm();
      await fetchEmployees();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetEmployeeForm = async () => {
    const newCode = await generateEmployeeCode();
    setEmployeeForm({
      employee_code: newCode,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      status: 'active',
      hire_date: new Date().toISOString().split('T')[0],
      salary_amount: '',
      salary_currency: 'USD',
      birth_date: '',
      address: '',
      manager_id: '',
      relay_id: ''
    });
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Briefcase className="h-6 w-6 text-primary" />
              <span>{t('admin.tabs.employees')}</span>
            </CardTitle>
            <CardDescription>
              {filteredEmployees.length} {t('hr.employees').toLowerCase()} {statusFilter !== 'all' || searchQuery || departmentFilter !== 'all' ? t('filtered') || 'filtré(s)' : t('employees.total') || 'au total'}
            </CardDescription>
          </div>
          <Button
            onClick={async () => {
              await resetEmployeeForm();
              setEmployeeDialog(true);
            }}
            className="bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('hr.new_employee')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <EmployeeFilters
          statusFilter={statusFilter}
          searchQuery={searchQuery}
          departmentFilter={departmentFilter}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onStatusChange={setStatusFilter}
          onSearchChange={setSearchQuery}
          onDepartmentChange={setDepartmentFilter}
          onSortByChange={setSortBy}
          onSortOrderToggle={handleToggleSortOrder}
          onClearFilters={handleClearFilters}
        />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('employee') || 'Employé'}</TableHead>
              <TableHead>{t('code') || 'Code'}</TableHead>
              <TableHead>{t('position') || 'Poste'}</TableHead>
              <TableHead>{t('department') || 'Département'}</TableHead>
              <TableHead>{t('account') || 'Compte'}</TableHead>
              <TableHead>{t('status') || 'Statut'}</TableHead>
              <TableHead>{t('actions') || 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('loading') || 'Chargement...'}
                </TableCell>
              </TableRow>
            ) : filteredEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {t('no_employees_found') || 'Aucun employé trouvé'}
                </TableCell>
              </TableRow>
            ) : (
              filteredEmployees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Briefcase className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                        <p className="text-sm text-muted-foreground">{employee.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{employee.employee_code}</Badge>
                  </TableCell>
                  <TableCell>{employee.position}</TableCell>
                  <TableCell>{employee.department}</TableCell>
                  <TableCell>
                    {employee.profile ? (
                      <div className="flex items-center space-x-2">
                        <Badge variant="default" className="text-xs">
                          <Link className="h-3 w-3 mr-1" />
                          {SYSTEM_ROLE_LABELS[employee.profile.system_role as SystemRole]}
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCreateAccount(employee)}
                          className="text-muted-foreground hover:text-primary text-xs px-2"
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          {t('create_account') || 'Créer'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLinkAccount(employee)}
                          className="text-muted-foreground hover:text-primary text-xs px-2"
                        >
                          <Link className="h-3 w-3 mr-1" />
                          {t('link_account') || 'Lier'}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={employee.status === 'active' ? 'outline' : 'secondary'}>
                      {employee.status === 'active' ? (t('employees.active') || 'Actif') : employee.status === 'inactive' ? (t('employees.inactive') || 'Inactif') : (t('terminated') || 'Terminé')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditEmployee(employee)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {t('edit') || 'Modifier'}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('delete') || 'Supprimer'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Dialog pour créer/modifier un employé */}
      <Dialog open={employeeDialog} onOpenChange={setEmployeeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? 'Modifier l\'employé' : t('hr.new_employee')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Prénom</Label>
              <Input
                id="first_name"
                value={employeeForm.first_name}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="Prénom de l'employé"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Nom</Label>
              <Input
                id="last_name"
                value={employeeForm.last_name}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Nom de l'employé"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="employee_code">Code employé</Label>
              <Input
                id="employee_code"
                value={employeeForm.employee_code}
                disabled
                placeholder="Généré automatiquement"
                className="bg-gray-50 text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@exemple.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1234567890"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position">Poste</Label>
              <Select value={employeeForm.position} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, position: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un poste" />
                </SelectTrigger>
                <SelectContent>
                  {jobPositions.map((position) => (
                    <SelectItem key={position.id} value={position.title}>
                      {position.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Département</Label>
              <Select value={employeeForm.department} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, department: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Statut</Label>
              <Select value={employeeForm.status} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Inactif</SelectItem>
                  <SelectItem value="terminated">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hire_date">Date d'embauche</Label>
              <Input
                id="hire_date"
                type="date"
                value={employeeForm.hire_date}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, hire_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="salary_amount">Salaire</Label>
              <div className="flex gap-2">
                <Input
                  id="salary_amount"
                  type="number"
                  placeholder="Montant"
                  value={employeeForm.salary_amount}
                  onChange={(e) => setEmployeeForm(prev => ({ ...prev, salary_amount: e.target.value }))}
                />
                <Select value={employeeForm.salary_currency} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, salary_currency: value }))}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDF">CDF</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={employeeForm.address}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Adresse complète"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEmployee}>
              {editingEmployee ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog pour créer un compte utilisateur */}
      <Dialog open={createAccountDialog} onOpenChange={setCreateAccountDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('create_user_account') || 'Créer un compte utilisateur'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployeeForAccount && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium">
                  {selectedEmployeeForAccount.first_name} {selectedEmployeeForAccount.last_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedEmployeeForAccount.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedEmployeeForAccount.position} - {selectedEmployeeForAccount.department}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="system_role">{t('system_role') || 'Rôle système'}</Label>
                <Select value={accountForm.system_role} onValueChange={(value) => setAccountForm(prev => ({ ...prev, system_role: value as SystemRole }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">{SYSTEM_ROLE_LABELS.client}</SelectItem>
                    <SelectItem value="vendor">{SYSTEM_ROLE_LABELS.vendor}</SelectItem>
                    <SelectItem value="relay">{SYSTEM_ROLE_LABELS.relay}</SelectItem>
                    <SelectItem value="hr">{SYSTEM_ROLE_LABELS.hr}</SelectItem>
                    <SelectItem value="admin">{SYSTEM_ROLE_LABELS.admin}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('temporary_password') || 'Mot de passe temporaire'}</Label>
                <Input
                  id="password"
                  type="password"
                  value={accountForm.password}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={t('enter_password') || 'Saisir le mot de passe'}
                />
                <p className="text-xs text-muted-foreground">
                  {t('password_help') || "L'employé devra changer ce mot de passe à la première connexion"}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAccountDialog(false)}>
              {t('cancel') || 'Annuler'}
            </Button>
            <Button onClick={handleSaveAccount}>
              <UserPlus className="h-4 w-4 mr-2" />
              {t('create_account') || 'Créer le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog pour lier un compte utilisateur existant */}
      <Dialog open={linkAccountDialog} onOpenChange={setLinkAccountDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('link_user_account') || 'Lier un compte utilisateur'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployeeForAccount && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium">
                  {selectedEmployeeForAccount.first_name} {selectedEmployeeForAccount.last_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedEmployeeForAccount.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedEmployeeForAccount.position} - {selectedEmployeeForAccount.department}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user_select">{t('select_user') || 'Sélectionner un utilisateur'}</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_user_placeholder') || 'Choisir un utilisateur...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex flex-col">
                          <span>{user.full_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {user.email} - {SYSTEM_ROLE_LABELS[user.system_role as SystemRole]}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {availableUsers.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  {t('no_available_users') || 'Aucun utilisateur disponible pour la liaison'}
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkAccountDialog(false)}>
              {t('cancel') || 'Annuler'}
            </Button>
            <Button onClick={linkEmployeeAccount} disabled={!selectedUserId}>
              <Link className="h-4 w-4 mr-2" />
              {t('link_account') || 'Lier le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmployeesManager;
